import {Autocomplete, TextField} from "@mui/material";
import React, {Component} from "react";
import {v4 as uuidv4} from "uuid";
import {withAppContext} from "../util/withAppContext";
import spinner from "../assets/Spinner-1s-200px.svg";
import {prepareBuffer} from "../util/Buffer";
import enterpriseComponentClassFactory from "../eoec/EnterpriseComponentClassFactory";

// A picklist-backed combo box: SELECT a suggestion from the type-to-filter dropdown or (for free
// picklists) enter a value. Renders a MUI Autocomplete with freeSolo. Options carry {value,
// displayValue}: the dropdown shows/filters by displayValue (e.g. a term Name) while the STORED
// value is `value` (e.g. a USID). Wire it with control type "ComboBox".
//
// Two behaviors, driven by picklist metadata:
//  - mustMatchOption: the stored value is set ONLY when the entry resolves to a real option (use for
//    value/USID-backed pickers like a relationship Child term; free text is never stored).
//  - serverFilter: for long EC-backed lists, re-query the backend as the user types (typeField
//    <serverFilterOperator> text, serverFilterLimit rows) instead of pre-loading + client filtering.
//
// The visible text is a local state (inputText) so typing/filtering is decoupled from the stored
// value. Sort, persona visibility scoping and exclusions mirror PlatformSelect.
class PlatformComboBox extends Component {
    constructor(props) {
        super(props);
        this.pickValues = props.appContext.sessionContext.getPickValues();
        this.picklistEC = null;
        this.debounceTimer = null;
        this.labelToValue = {};
        this.valueToLabel = {};
        this.state = {
            id: uuidv4(),
            buffer: [],
            fetchBackgroundOperation: false,
            // True once the first query has answered. The spinner stands in for the control only
            // before that: afterwards every keystroke of a server search is a load, and a search with
            // no matches has an empty buffer -- so keying the spinner on those two alone replaced the
            // input while somebody was typing in it and threw their focus away mid-word.
            loadedOnce: false,
            // The label of the value the form opened with, when a server-filtered list does not
            // happen to include it. See resolvePresetLabel.
            presetLabel: null,
            inputText: props.value === null || props.value === undefined ? "" : String(props.value),
        };
    }

    componentDidMount() {
        const picklist = this.props.picklist;
        if (picklist && picklist.getStatic() === false) {
            let enterpriseComponentObject = enterpriseComponentClassFactory(picklist.getEnterpriseComponent());
            this.picklistEC = new (enterpriseComponentObject)(picklist.getEnterpriseComponent());
            this.picklistEC.addLoadStartedListener(this);
            this.picklistEC.addLoadCompletedListener(this);
            this.runQuery("");   // initial (unfiltered) load
            if (picklist.getServerFilter && picklist.getServerFilter()) this.resolvePresetLabel(this.props.value);
        }
    }

    // A SERVER-FILTERED LIST LOADS ONE PAGE, so the value a form opens with may not be on it -- and a
    // value with no option shows as its raw USID. Ask for that one row by value instead, on a
    // component of its own so it cannot disturb the list being searched.
    resolvePresetLabel(value) {
        const picklist = this.props.picklist;
        if (value === null || value === undefined || value === "" || !picklist) return;
        const enterpriseComponentObject = enterpriseComponentClassFactory(picklist.getEnterpriseComponent());
        const ec = new (enterpriseComponentObject)(picklist.getEnterpriseComponent());
        ec.addLoadCompletedListener({
            enterpriseComponentLoadCompleted: (loaded) => {
                const rows = prepareBuffer(loaded);
                const label = rows.length ? rows[0].get(picklist.getTypeField()) : null;
                if (label) this.setState({presetLabel: {value: String(value), label}});
            },
        });
        ec.newQuery();
        ec.setAttributeValue(picklist.getTypeValue(), value);
        if (picklist.getVisibilityType && picklist.getVisibilityType()) {
            const activeVisibility = this.props.appContext.sessionContext.getActiveVisibility();
            if (activeVisibility && activeVisibility.Mode && activeVisibility.Value) {
                ec.setVisibilityMode(activeVisibility.Mode);
                ec.setVisibilityValue(activeVisibility.Value);
            }
        }
        ec.executeQuery(1, 1);
    }

    componentDidUpdate(prevProps) {
        // Reflect an external value change (or options finishing loading for a preset value) in the
        // visible text — but never clobber what the user is actively typing.
        const raw = this.props.value === null || this.props.value === undefined ? "" : String(this.props.value);
        const label = this.valueToLabel[raw];
        if (prevProps.value !== this.props.value) {
            if (raw === "") {
                if (this.state.inputText !== "") this.setState({inputText: ""});
            } else if (label !== undefined && this.state.inputText !== label) {
                this.setState({inputText: label});
            }
        } else if (raw !== "" && label !== undefined && this.state.inputText === raw) {
            this.setState({inputText: label});
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
    }

    enterpriseComponentLoadStarted = () => {
        this.setState({fetchBackgroundOperation: true});
    };

    enterpriseComponentLoadCompleted = (ec) => {
        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false, loadedOnce: true});
    };

    runQuery(filterText) {
        const ec = this.picklistEC;
        const picklist = this.props.picklist;
        if (!ec || !picklist) return;

        ec.newQuery();   // resets sort/search/visibility, so (re)apply them below

        let searchSpecification = picklist.getSearchSpecification();
        if (searchSpecification !== null && searchSpecification !== undefined) {
            let keys = Object.keys(searchSpecification);
            for (let i = 0; i < keys.length; i++) {
                let ss = searchSpecification[keys[i]];
                // eslint-disable-next-line no-template-curly-in-string
                if (ss && ss.includes && ss.includes("${sessionContext:realm}")) {
                    searchSpecification[keys[i]] = this.props.appContext.sessionContext.getRealm();
                }
            }
            ec.setSearchSpecification(searchSpecification);
        }

        const serverFilter = picklist.getServerFilter && picklist.getServerFilter();
        if (serverFilter && filterText) {
            ec.setAttributeValue(picklist.getTypeField(), filterText);
            ec.setAttributeValue(picklist.getTypeField() + "_query", picklist.getServerFilterOperator());
        }

        // Metadata declares sort as a map ({"Name":"ASC"}); the EC wants an array. (See PlatformSelect.)
        let rawSort = picklist.getSortSpecification();
        let sortSpecification = [];
        if (Array.isArray(rawSort)) sortSpecification = rawSort;
        else if (rawSort && typeof rawSort === "object") sortSpecification = Object.keys(rawSort).map(key => ({SortAttribute: key, Direction: rawSort[key]}));
        ec.setSortSpecification(sortSpecification);

        if (picklist.getVisibilityType && picklist.getVisibilityType()) {
            let activeVisibility = this.props.appContext.sessionContext.getActiveVisibility();
            if (activeVisibility && activeVisibility.Mode && activeVisibility.Value) {
                ec.setVisibilityMode(activeVisibility.Mode);
                ec.setVisibilityValue(activeVisibility.Value);
            }
        }

        let limit = serverFilter && picklist.getServerFilterLimit ? picklist.getServerFilterLimit() : undefined;
        ec.executeQuery(1, limit);
    }

    excludedValues() {
        const picklist = this.props.picklist;
        let excluded = [];
        let parentControls = this.props.parent && this.props.parent.state ? this.props.parent.state.controls : null;
        let excludeFormFields = picklist.getExcludeFormFields ? picklist.getExcludeFormFields() : null;
        if (excludeFormFields && parentControls) {
            for (let i = 0; i < excludeFormFields.length; i++) {
                let control = parentControls[excludeFormFields[i]];
                let value = control ? control.value : undefined;
                if (value !== undefined && value !== null && value !== "") excluded.push(value);
            }
        }
        let excludeECAttributes = picklist.getExcludeEnterpriseComponentAttributes ? picklist.getExcludeEnterpriseComponentAttributes() : null;
        let formEC = this.props.parent && this.props.parent.applet ? this.props.parent.applet.getEnterpriseComponent() : null;
        if (excludeECAttributes && formEC) {
            for (let i = 0; i < excludeECAttributes.length; i++) {
                let value = formEC.getAttributeValue(excludeECAttributes[i]);
                if (value !== undefined && value !== null && value !== "") excluded.push(value);
            }
        }
        return excluded;
    }

    buildOptions() {
        const picklist = this.props.picklist;
        let options = [];
        if (!picklist) return options;

        if (picklist.getStatic() === true) {
            let typeField = picklist.getTypeField();
            let typeValue = picklist.getTypeValue();
            for (let i = 0; i < this.pickValues.length; i++) {
                if (this.pickValues[i][typeField] !== typeValue) continue;
                options.push({value: this.pickValues[i]["Value"], displayValue: this.pickValues[i]["Name"]});
            }
        } else if (picklist.getCompanySource() !== null) {
            let account = this.props.appContext.sessionContext.getAccount();
            let source = account ? account[picklist.getCompanySource()] : null;
            if (Array.isArray(source)) source.forEach(e => options.push({value: e["USID"], displayValue: e["Name"]}));
        } else {
            let typeField = picklist.getTypeField();
            let typeValue = picklist.getTypeValue();
            let excluded = this.excludedValues();
            for (let i = 0; i < this.state.buffer.length; i++) {
                let optionValue = this.state.buffer[i].get(typeValue);
                if (excluded.indexOf(optionValue) !== -1) continue;
                options.push({value: optionValue, displayValue: this.state.buffer[i].get(typeField)});
            }
        }
        return options.filter(o => o.displayValue !== null && o.displayValue !== undefined && o.displayValue !== "");
    }

    mustMatch() {
        const p = this.props.picklist;
        return !!(p && p.getMustMatchOption && p.getMustMatchOption());
    }

    emit(value) {
        // PlatformDetail.inputChangedHandler reads event.target.value (synthesized event, as elsewhere).
        if (this.props.onChange) this.props.onChange({target: {value: value === null || value === undefined ? "" : value}});
    }

    // Typing / clearing: drive the visible text (and server search); set the stored value only for
    // free-value pickers (match-required pickers set it on selection via onSelect).
    onInputChange = (event, text, reason) => {
        if (text !== this.state.inputText) this.setState({inputText: text});

        const picklist = this.props.picklist;
        if (reason === "input") {
            if (this.mustMatch()) {
                let resolved = this.labelToValue[text];
                this.emit(resolved !== undefined ? resolved : "");   // only a real option counts
            } else {
                let resolved = this.labelToValue[text];
                this.emit(resolved !== undefined ? resolved : text); // free entry allowed
            }
            if (picklist && picklist.getServerFilter && picklist.getServerFilter()) {
                if (this.debounceTimer) clearTimeout(this.debounceTimer);
                this.debounceTimer = setTimeout(() => this.runQuery(text), 250);
            }
        } else if (reason === "clear") {
            this.emit("");
            if (picklist && picklist.getServerFilter && picklist.getServerFilter()) {
                if (this.debounceTimer) clearTimeout(this.debounceTimer);
                this.debounceTimer = setTimeout(() => this.runQuery(""), 250);
            }
        }
    };

    // Selection (or Enter): resolve the chosen label to its stored value.
    onSelect = (event, newValue) => {
        if (newValue === null || newValue === undefined) { this.emit(""); return; }
        let resolved = this.labelToValue[newValue];
        if (resolved !== undefined) {
            this.emit(resolved);
            this.setState({inputText: newValue});
        } else {
            this.emit(this.mustMatch() ? "" : newValue);
        }
    };

    // On blur, drop stray unmatched text for match-required pickers (revert to the current value's
    // label, or empty) so the field never shows text that isn't a real selection.
    onBlur = (event) => {
        if (this.mustMatch() && this.labelToValue[this.state.inputText] === undefined) {
            const raw = this.props.value === null || this.props.value === undefined ? "" : String(this.props.value);
            let label = this.valueToLabel[raw];
            this.setState({inputText: label !== undefined ? label : ""});
        }
        if (this.props.onBlur) this.props.onBlur(event);
    };

    render() {
        const picklist = this.props.picklist;

        if (picklist && picklist.getStatic() === false && !this.state.loadedOnce) {
            return <div><img width="35" height="35" src={spinner} alt="spinner"/></div>;
        }

        const optionObjects = this.buildOptions();
        const labelOptions = [];
        this.labelToValue = {};
        this.valueToLabel = {};
        for (let i = 0; i < optionObjects.length; i++) {
            let label = optionObjects[i].displayValue;
            let val = optionObjects[i].value;
            labelOptions.push(label);
            this.labelToValue[label] = val;
            this.valueToLabel[String(val)] = label;
        }
        const preset = this.state.presetLabel;
        if (preset && this.valueToLabel[preset.value] === undefined) {
            this.valueToLabel[preset.value] = preset.label;
            if (this.labelToValue[preset.label] === undefined) this.labelToValue[preset.label] = preset.value;
        }

        return (
            <Autocomplete
                freeSolo
                fullWidth={this.props.fullWidth !== undefined ? this.props.fullWidth : true}
                id={this.props.id}
                options={labelOptions}
                disabled={this.props.disabled}
                inputValue={this.state.inputText}
                onInputChange={this.onInputChange}
                onChange={this.onSelect}
                onBlur={this.onBlur}
                loading={this.state.fetchBackgroundOperation}
                // Server already filtered — don't also filter client-side (would hide valid matches).
                filterOptions={picklist && picklist.getServerFilter && picklist.getServerFilter() ? (opts) => opts : undefined}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={this.props.label}
                        placeholder={this.props.placeholder}
                        size={this.props.size}
                        required={this.props.required}
                        error={this.props.error}
                        helperText={this.props.helperText}
                        variant={this.props.variant || "outlined"}
                    />
                )}
            />
        );
    }
}

export default withAppContext(PlatformComboBox);
